import { Dispatch } from "redux";
import { Alert } from "reactstrap";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import useTypedNavigate from "../hooks/typed-navigate";
import { useSeo } from "../hooks/useSeo";

interface Props {
  auth: AuthenticationState;
  forgotPassword: (email: string) => (dispatch: Dispatch<AuthenticationAction>) => Promise<void>;
  confirmPassword: (email: string, code: string, newPassword: string) => (dispatch: Dispatch<AuthenticationAction>) => Promise<void>;
  clearError: () => void;
}

export default function ForgotPasswordPage(props: Props) {
  useSeo({ title: "Reset Password", path: "/forgot-password", noindex: true });
  const { auth, forgotPassword, confirmPassword, clearError } = props;
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [pwMismatch, setPwMismatch] = useState(false);
  const navigate = useTypedNavigate();

  useEffect(() => {
    clearError();
  }, [clearError]);

  useEffect(() => {
    if (auth.passwordReset) {
      navigate("/login");
    }
  }, [auth.passwordReset, navigate]);

  const handleRequestCode = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitted(true);
    if (!email) return;
    await forgotPassword(email);
  };

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitted(true);
    setPwMismatch(false);
    if (!code || !newPassword || !confirmPw) return;
    if (newPassword !== confirmPw) {
      setPwMismatch(true);
      return;
    }
    await confirmPassword(email, code, newPassword);
  };

  if (auth.resetRequested) {
    return (
      <div className="container">
        <div className="row justify-content-md-center">
          <div className="col-md-auto login-container">
            <h2>Reset Password</h2>
            <p className="text-muted">Enter the code sent to {email} and choose a new password.</p>
            <form onSubmit={handleResetPassword}>
              <div className={"form-group" + (submitted && !code ? " has-error" : "")}>
                <label>
                  Verification Code *
                  <input
                    type="text"
                    className="form-control"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                </label>
                {submitted && !code && (
                  <div className="help-block" style={{ color: "red" }}>
                    Code is required
                  </div>
                )}
              </div>
              <div className={"form-group" + (submitted && !newPassword ? " has-error" : "")}>
                <label>
                  New Password *
                  <input
                    type="password"
                    className="form-control"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </label>
                {submitted && !newPassword && (
                  <div className="help-block" style={{ color: "red" }}>
                    Password is required
                  </div>
                )}
              </div>
              <div className={"form-group" + (submitted && !confirmPw ? " has-error" : "")}>
                <label>
                  Confirm Password *
                  <input
                    type="password"
                    className="form-control"
                    value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)}
                  />
                </label>
                {submitted && !confirmPw && (
                  <div className="help-block" style={{ color: "red" }}>
                    Please confirm your password
                  </div>
                )}
                {pwMismatch && (
                  <div className="help-block" style={{ color: "red" }}>
                    Passwords do not match
                  </div>
                )}
              </div>
              <div className="form-group">
                <button className="btn btn-primary" disabled={auth.resettingPassword}>
                  {auth.resettingPassword ? (
                    <><span className="spinner-border spinner-border-sm" role="status" /> Resetting...</>
                  ) : (
                    "Reset Password"
                  )}
                </button>
              </div>
            </form>
            <Alert color="danger" isOpen={!!auth.errorMessage}>
              {auth.errorMessage}
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="row justify-content-md-center">
        <div className="col-md-auto login-container">
          <h2>Forgot Password</h2>
          <p className="text-muted">Enter your email and we'll send you a reset code.</p>
          <form onSubmit={handleRequestCode}>
            <div className={"form-group" + (submitted && !email ? " has-error" : "")}>
              <label>
                Email *
                <input
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
              {submitted && !email && (
                <div className="help-block" style={{ color: "red" }}>
                  Email is required
                </div>
              )}
            </div>
            <div className="form-group">
              <button className="btn btn-primary" disabled={auth.requestingReset}>
                {auth.requestingReset ? (
                  <><span className="spinner-border spinner-border-sm" role="status" /> Sending...</>
                ) : (
                  "Send Reset Code"
                )}
              </button>
            </div>
          </form>
          <Alert color="danger" isOpen={!!auth.errorMessage}>
            {auth.errorMessage}
          </Alert>
          <p className="mt-3">
            <Link to="/login">Back to Login</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
